import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './src/authConfig';
import { seedDatabase } from './src/seedData';
import { ErrorBoundary } from './src/components/ErrorBoundary';

// Exponer la función reparada en la consola para inyección manual
(window as any).seedDatabase = seedDatabase;

import { CustomNavigationClient } from './src/authConfig';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const msalInstance = new PublicClientApplication(msalConfig);
msalInstance.setNavigationClient(new CustomNavigationClient());

if (Capacitor.isNativePlatform()) {
    localStorage.setItem('isNative', 'true');
    CapacitorApp.addListener('appUrlOpen', async (event) => {
        localStorage.setItem('isNative', 'true');
        if (event.url.includes('k-lydapp.ldconstructora.cl') || event.url.startsWith('klydapp://')) {
            Browser.close().catch(console.error);
            
            // 1. MSAL Handling
            msalInstance.handleRedirectPromise({ hash: event.url }).then((response) => {
                if (response && response.account) {
                    msalInstance.setActiveAccount(response.account);
                }
            }).catch(console.error);

            // 2. Supabase Magic Link & Universal Authentication Links Handling
            const isAuthUrl = 
                event.url.includes('/auth/invite') ||
                event.url.includes('/auth/otp') ||
                event.url.includes('/auth/callback') ||
                event.url.includes('/auth/confirm') ||
                event.url.includes('/auth-callback') ||
                event.url.startsWith('klydapp://auth');

            if (isAuthUrl) {
                try {
                    let token = null;
                    let type = 'magiclink';
                    
                    if (event.url.startsWith('klydapp://')) {
                        // Extract query params manually since URL() might fail on custom schemes
                        const queryString = event.url.split('?')[1];
                        if (queryString) {
                            const params = new URLSearchParams(queryString);
                            token = params.get('token');
                            type = params.get('type') || 'magiclink';
                        }
                    } else {
                        const urlObj = new URL(event.url);
                        token = urlObj.searchParams.get('token');
                        
                        // Deduce default authentication type from universal path prefix if not provided in query
                        const explicitType = urlObj.searchParams.get('type');
                        if (explicitType) {
                            type = explicitType;
                        } else {
                            const path = urlObj.pathname;
                            if (path.includes('/invite')) type = 'invite';
                            else if (path.includes('/otp')) type = 'magiclink';
                            else if (path.includes('/callback')) type = 'magiclink';
                            else if (path.includes('/confirm')) type = 'signup';
                            else type = 'magiclink';
                        }
                    }
                    
                    if (token) {
                        const { supabase } = await import('./src/supabaseClient');
                        const { error } = await (supabase.auth as any).verifyOtp({ token_hash: token, type: type });
                        if (error) {
                            console.error("Error verifying OTP from deep link:", error);
                            alert("El enlace de acceso ha expirado o ya fue utilizado. Por favor, solicita uno nuevo.");
                        }
                    }
                } catch (e) {
                    console.error("Error parsing auth link:", e);
                }
            } else if (event.url.includes('#access_token=')) {
                try {
                    const hashPart = event.url.split('#')[1];
                    const params = new URLSearchParams(hashPart);
                    const access_token = params.get('access_token');
                    const refresh_token = params.get('refresh_token');
                    
                    if (access_token && refresh_token) {
                        const { supabase } = await import('./src/supabaseClient');
                        await supabase.auth.setSession({ access_token, refresh_token });
                    }
                } catch (e) {
                    console.error("Error setting Supabase session from deep link:", e);
                }
            }
        }
    });
}

msalInstance.initialize().then(() => {
    // Process the redirect return from Microsoft Entra ID
    msalInstance.handleRedirectPromise().then((response) => {
        if (response && response.account) {
            msalInstance.setActiveAccount(response.account);
        } else {
            // Fallback: Check if there's already an active account in cache
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) {
                msalInstance.setActiveAccount(accounts[0]);
            }
        }

        const rootElement = document.getElementById('root');
        if (!rootElement) {
          throw new Error("Could not find root element to mount to");
        }

        const root = ReactDOM.createRoot(rootElement);
        root.render(
          <React.StrictMode>
            <ErrorBoundary>
              <MsalProvider instance={msalInstance}>
                <App />
              </MsalProvider>
            </ErrorBoundary>
          </React.StrictMode>
        );
    }).catch(error => {
        console.error("Error in MSAL redirect:", error);
        
        // Render anyway to show potential Unauthenticated state
        const rootElement = document.getElementById('root');
        if (rootElement) {
            const root = ReactDOM.createRoot(rootElement);
            root.render(
              <React.StrictMode>
                <ErrorBoundary>
                  <MsalProvider instance={msalInstance}>
                    <App />
                  </MsalProvider>
                </ErrorBoundary>
              </React.StrictMode>
            );
        }
    });
});